export const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
export const WORKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR'];

const FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];

const pad = value => String(value).padStart(2, '0');

const formatUntil = date => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 0);
  return (
    `${end.getUTCFullYear()}${pad(end.getUTCMonth() + 1)}${pad(end.getUTCDate())}` +
    `T${pad(end.getUTCHours())}${pad(end.getUTCMinutes())}${pad(end.getUTCSeconds())}Z`
  );
};

const parseUntil = value => {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match.map(Number);
  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
};

export const buildRule = ({
  freq,
  interval = 1,
  byDay = [],
  endType = 'never',
  until = null,
  count = null,
}) => {
  const parts = [`FREQ=${freq}`];
  if (interval > 1) parts.push(`INTERVAL=${interval}`);
  if (freq === 'WEEKLY' && byDay.length) parts.push(`BYDAY=${byDay.join(',')}`);
  if (endType === 'until' && until) parts.push(`UNTIL=${formatUntil(until)}`);
  if (endType === 'count' && count) parts.push(`COUNT=${count}`);
  return parts.join(';');
};

export const parseRule = rule => {
  if (!rule) return null;
  const parts = Object.fromEntries(
    rule.split(';').map(part => part.split('='))
  );
  if (!FREQUENCIES.includes(parts.FREQ)) return null;
  const until = parts.UNTIL ? parseUntil(parts.UNTIL) : null;
  let endType = 'never';
  if (until) endType = 'until';
  else if (parts.COUNT) endType = 'count';
  return {
    freq: parts.FREQ,
    interval: Number(parts.INTERVAL) || 1,
    byDay: parts.BYDAY ? parts.BYDAY.split(',') : [],
    endType,
    until,
    count: parts.COUNT ? Number(parts.COUNT) : null,
  };
};

export const weekdayCode = date => WEEKDAY_CODES[date.getDay()];

export const presetRule = (preset, date) => {
  switch (preset) {
    case 'daily':
      return buildRule({ freq: 'DAILY' });
    case 'weekly':
      return buildRule({ freq: 'WEEKLY', byDay: [weekdayCode(date)] });
    case 'weekdays':
      return buildRule({ freq: 'WEEKLY', byDay: WORKDAY_CODES });
    case 'monthly':
      return buildRule({ freq: 'MONTHLY' });
    case 'yearly':
      return buildRule({ freq: 'YEARLY' });
    default:
      return null;
  }
};

export const presetFor = (rule, date) => {
  if (!rule) return 'none';
  const found = ['daily', 'weekly', 'weekdays', 'monthly', 'yearly'].find(
    preset => presetRule(preset, date) === rule
  );
  return found || 'custom';
};

export const weekdayName = (code, locale) => {
  const sunday = new Date(2023, 0, 1);
  sunday.setDate(1 + WEEKDAY_CODES.indexOf(code));
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(sunday);
};

const joinList = (items, locale) =>
  new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
    items
  );

export const describeRule = (rule, startDate, t, locale) => {
  const parsed = parseRule(rule);
  if (!parsed) return '';
  const { freq, interval, byDay, endType, until, count } = parsed;
  const dateFormat = options => new Intl.DateTimeFormat(locale, options);
  const isWorkdays =
    byDay.length === WORKDAY_CODES.length &&
    WORKDAY_CODES.every(code => byDay.includes(code));
  const days = joinList(
    (byDay.length ? byDay : [weekdayCode(startDate)]).map(code =>
      weekdayName(code, locale)
    ),
    locale
  );

  let text;
  if (freq === 'DAILY') {
    text = t('TASKS.RECURRENCE.DESCRIBE.DAILY', { count: interval });
  } else if (freq === 'WEEKLY') {
    text = isWorkdays
      ? t('TASKS.RECURRENCE.DESCRIBE.WORKDAYS', { count: interval })
      : t('TASKS.RECURRENCE.DESCRIBE.WEEKLY', { count: interval, days });
  } else if (freq === 'MONTHLY') {
    text = t('TASKS.RECURRENCE.DESCRIBE.MONTHLY', {
      count: interval,
      day: startDate.getDate(),
    });
  } else {
    text = t('TASKS.RECURRENCE.DESCRIBE.YEARLY', {
      count: interval,
      date: dateFormat({ day: 'numeric', month: 'long' }).format(startDate),
    });
  }

  if (endType === 'until') {
    const untilText = dateFormat({ dateStyle: 'long' }).format(until);
    return `${text}, ${t('TASKS.RECURRENCE.DESCRIBE.UNTIL', { date: untilText })}`;
  }
  if (endType === 'count') {
    return `${text}, ${t('TASKS.RECURRENCE.DESCRIBE.TIMES', { count })}`;
  }
  return text;
};

export const taskKey = task =>
  task.recurrenceRule && task.occurrenceDate
    ? `${task.id}-${task.occurrenceDate}`
    : String(task.id);

// All-day tasks are persisted as UTC midnight of the selected calendar day
// (see Task#normalize_due_date_for_all_day). Reading the day back from the
// browser's local timezone would shift it by a day west of UTC, so for
// all-day tasks the day must be read from the UTC components instead.
export const getTaskDueDate = task => {
  const date = new Date(task.dueDate * 1000);
  if (!task.allDay) return date;
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const getTaskEndDate = task =>
  task.endDate && !task.allDay ? new Date(task.endDate * 1000) : null;

// The "end time" picker only returns a time of day; the end shares the
// start's calendar day (tasks cannot cross midnight).
export const buildEndDate = (dueDate, endTime) => {
  if (!dueDate || !endTime) return null;
  const end = new Date(dueDate);
  end.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
  return end;
};

export const isEndAfterStart = (dueDate, endTime) => {
  const end = buildEndDate(dueDate, endTime);
  return !end || end > dueDate;
};

export const formatTaskEndTime = (task, locale) => {
  const end = getTaskEndDate(task);
  if (!end) return '';
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(end);
  return ` – ${time}`;
};

const HOUR_MS = 60 * 60 * 1000;

// Default end time: one hour after the start, as long as it stays on the same day.
export const suggestEndTime = dueDate => {
  const end = new Date(dueDate.getTime() + HOUR_MS);
  return end.toDateString() === dueDate.toDateString() ? end : null;
};
